import React, { useState, useEffect } from 'react';
import {
    Card, Table, Button, Modal, Form, Input, InputNumber,
    Select, Space, Tag, message, Drawer, Row, Col, Descriptions,
    Timeline, Badge
} from 'antd';
import {
    CheckCircleOutlined, CloseCircleOutlined, EyeOutlined,
    MessageOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import dayjs from 'dayjs';
import execute from '../../hooks/execute';
import { protectedResources } from '../../config/msalConfig';

const SectionCard = styled(Card)`
    margin-bottom: 16px;
`;

const FilterContainer = styled.div`
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
    flex-wrap: wrap;
`;

const StatCard = styled.div`
    background: ${props => props.$bgColor || '#f5f5f5'};
    border-radius: 8px;
    padding: 16px;
    text-align: center;

    .stat-value {
        font-size: 28px;
        font-weight: 600;
        color: ${props => props.$color || '#333'};
    }

    .stat-label {
        color: #666;
        font-size: 13px;
        margin-top: 4px;
    }
`;

const SubscriptionRequests = ({ plans }) => {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [decisionModalVisible, setDecisionModalVisible] = useState(false);
    const [decisionType, setDecisionType] = useState(null);
    const [filters, setFilters] = useState({});
    const [form] = Form.useForm();

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.requestType) params.append('requestType', filters.requestType);

            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/ai-subscription/requests?${params.toString()}`
            );
            if (response.success) {
                setRequests(response.data);
            }
        } catch (error) {
            message.error('Failed to fetch requests');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRequests();
    }, [filters]);

    const handleViewRequest = (request) => {
        setSelectedRequest(request);
        setDrawerVisible(true);
    };

    const handleDecision = (type) => {
        setDecisionType(type);
        form.resetFields();
        setDecisionModalVisible(true);
    };

    const submitDecision = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const endpoint = `${protectedResources.api.baseEndPoint}/ai-subscription/requests/${selectedRequest.RequestId}/${decisionType}`;

            const response = await execute('POST', endpoint, { comments: values.comments });

            if (response.success) {
                message.success(`Request ${decisionType === 'approve' ? 'approved' : 'rejected'} successfully`);
                setDecisionModalVisible(false);
                setDrawerVisible(false);
                fetchRequests();
            } else {
                message.error(response.message || 'Failed to process decision');
            }
        } catch (error) {
            message.error('Failed to process decision');
        } finally {
            setLoading(false);
        }
    };

    const getStatusTag = (status) => {
        const config = {
            Submitted: { color: 'blue', text: 'Submitted' },
            InReview: { color: 'processing', text: 'In Review' },
            Approved: { color: 'success', text: 'Approved' },
            Rejected: { color: 'error', text: 'Rejected' },
            MoreInfoRequested: { color: 'warning', text: 'More Info Requested' },
            Completed: { color: 'default', text: 'Completed' }
        };
        const { color, text } = config[status] || { color: 'default', text: status };
        return <Tag color={color}>{text}</Tag>;
    };

    const getRequestTypeTag = (type) => {
        const config = {
            TRIAL: { color: 'purple', text: 'Trial' },
            PLAN_CHANGE: { color: 'orange', text: 'Plan Change' },
            TOKEN_INCREASE: { color: 'cyan', text: 'Token Increase' },
            LICENSE_INCREASE: { color: 'green', text: 'License Increase' }
        };
        const { color, text } = config[type] || { color: 'default', text: type };
        return <Tag color={color}>{text}</Tag>;
    };

    const getStatusCounts = () => {
        return {
            pending: requests.filter(r => r.Status === 'Submitted' || r.Status === 'InReview').length,
            approved: requests.filter(r => r.Status === 'Approved').length,
            rejected: requests.filter(r => r.Status === 'Rejected').length,
            total: requests.length
        };
    };

    const counts = getStatusCounts();

    const columns = [
        {
            title: 'Request ID',
            dataIndex: 'RequestId',
            key: 'requestId',
            render: (id) => `#${id}`
        },
        {
            title: 'Type',
            dataIndex: 'RequestType',
            key: 'requestType',
            render: (type) => getRequestTypeTag(type)
        },
        {
            title: 'Organization',
            dataIndex: 'OrgName',
            key: 'orgName'
        },
        {
            title: 'Customer',
            dataIndex: 'CustomerName',
            key: 'customerName',
            render: (name) => name || '-'
        },
        {
            title: 'Requested Plan',
            key: 'requestedPlan',
            render: (_, record) => record.RequestedPlanName || '-'
        },
        {
            title: 'Submitted',
            dataIndex: 'CreatedOn',
            key: 'createdOn',
            render: (date) => dayjs(date).format('MMM DD, YYYY HH:mm')
        },
        {
            title: 'Status',
            dataIndex: 'Status',
            key: 'status',
            render: (status) => getStatusTag(status)
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    icon={<EyeOutlined />}
                    size="small"
                    onClick={() => handleViewRequest(record)}
                >
                    View
                </Button>
            )
        }
    ];

    return (
        <div>
            {/* Stats Row */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <StatCard $bgColor="#e6f7ff" $color="#1890ff">
                        <div className="stat-value">{counts.pending}</div>
                        <div className="stat-label">Pending Review</div>
                    </StatCard>
                </Col>
                <Col span={6}>
                    <StatCard $bgColor="#f6ffed" $color="#52c41a">
                        <div className="stat-value">{counts.approved}</div>
                        <div className="stat-label">Approved</div>
                    </StatCard>
                </Col>
                <Col span={6}>
                    <StatCard $bgColor="#fff2f0" $color="#ff4d4f">
                        <div className="stat-value">{counts.rejected}</div>
                        <div className="stat-label">Rejected</div>
                    </StatCard>
                </Col>
                <Col span={6}>
                    <StatCard>
                        <div className="stat-value">{counts.total}</div>
                        <div className="stat-label">Total Requests</div>
                    </StatCard>
                </Col>
            </Row>

            <SectionCard title="Subscription Requests">
                <FilterContainer>
                    <Select
                        placeholder="Status"
                        allowClear
                        style={{ width: 180 }}
                        onChange={(val) => setFilters({ ...filters, status: val })}
                        options={[
                            { value: 'Submitted', label: 'Submitted' },
                            { value: 'InReview', label: 'In Review' },
                            { value: 'Approved', label: 'Approved' },
                            { value: 'Rejected', label: 'Rejected' },
                            { value: 'MoreInfoRequested', label: 'More Info Requested' }
                        ]}
                    />
                    <Select
                        placeholder="Request Type"
                        allowClear
                        style={{ width: 180 }}
                        onChange={(val) => setFilters({ ...filters, requestType: val })}
                        options={[
                            { value: 'TRIAL', label: 'Trial' },
                            { value: 'PLAN_CHANGE', label: 'Plan Change' },
                            { value: 'TOKEN_INCREASE', label: 'Token Increase' },
                            { value: 'LICENSE_INCREASE', label: 'License Increase' }
                        ]}
                    />
                </FilterContainer>

                <Table
                    dataSource={requests}
                    columns={columns}
                    rowKey="RequestId"
                    loading={loading}
                    pagination={{ pageSize: 10 }}
                />
            </SectionCard>

            {/* Request Detail Drawer */}
            <Drawer
                title={`Request #${selectedRequest?.RequestId}`}
                placement="right"
                width={600}
                onClose={() => setDrawerVisible(false)}
                open={drawerVisible}
                extra={
                    selectedRequest && (selectedRequest.Status === 'Submitted' || selectedRequest.Status === 'InReview') && (
                        <Space>
                            <Button
                                danger
                                icon={<CloseCircleOutlined />}
                                onClick={() => handleDecision('reject')}
                            >
                                Reject
                            </Button>
                            <Button
                                type="primary"
                                icon={<CheckCircleOutlined />}
                                onClick={() => handleDecision('approve')}
                            >
                                Approve
                            </Button>
                        </Space>
                    )
                }
            >
                {selectedRequest && (
                    <>
                        <Descriptions column={1} bordered size="small" style={{ marginBottom: 24 }}>
                            <Descriptions.Item label="Status">
                                {getStatusTag(selectedRequest.Status)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Request Type">
                                {getRequestTypeTag(selectedRequest.RequestType)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Organization">
                                {selectedRequest.OrgName}
                            </Descriptions.Item>
                            <Descriptions.Item label="Customer">
                                {selectedRequest.CustomerName || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Submitted On">
                                {dayjs(selectedRequest.CreatedOn).format('MMMM DD, YYYY HH:mm')}
                            </Descriptions.Item>
                        </Descriptions>

                        <Card title="Request Details" size="small" style={{ marginBottom: 16 }}>
                            {selectedRequest.RequestType === 'TRIAL' && (
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Requested Plan">
                                        {selectedRequest.RequestedPlanName}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Trial Days">
                                        {selectedRequest.TrialDays} days
                                    </Descriptions.Item>
                                </Descriptions>
                            )}
                            {selectedRequest.RequestType === 'PLAN_CHANGE' && (
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Requested Plan">
                                        {selectedRequest.RequestedPlanName}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Requested Licenses">
                                        {selectedRequest.RequestedLicenseCount || 'N/A'}
                                    </Descriptions.Item>
                                </Descriptions>
                            )}
                            {selectedRequest.RequestType === 'TOKEN_INCREASE' && (
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Requested Token Limit">
                                        {selectedRequest.RequestedTokenLimit?.toLocaleString()} tokens
                                    </Descriptions.Item>
                                </Descriptions>
                            )}
                            {selectedRequest.RequestType === 'LICENSE_INCREASE' && (
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Requested License Count">
                                        {selectedRequest.RequestedLicenseCount}
                                    </Descriptions.Item>
                                </Descriptions>
                            )}
                        </Card>

                        {selectedRequest.Justification && (
                            <Card title="Justification" size="small" style={{ marginBottom: 16 }}>
                                <p>{selectedRequest.Justification}</p>
                            </Card>
                        )}

                        {selectedRequest.ReviewerComments && (
                            <Card title="Reviewer Comments" size="small" style={{ marginBottom: 16 }}>
                                <p>{selectedRequest.ReviewerComments}</p>
                                {selectedRequest.DecisionOn && (
                                    <p style={{ color: '#999', fontSize: 12 }}>
                                        Decision made on {dayjs(selectedRequest.DecisionOn).format('MMMM DD, YYYY HH:mm')}
                                    </p>
                                )}
                            </Card>
                        )}
                    </>
                )}
            </Drawer>

            {/* Decision Modal */}
            <Modal
                title={decisionType === 'approve' ? 'Approve Request' : 'Reject Request'}
                open={decisionModalVisible}
                onCancel={() => setDecisionModalVisible(false)}
                onOk={submitDecision}
                confirmLoading={loading}
                okText={decisionType === 'approve' ? 'Approve' : 'Reject'}
                okButtonProps={{
                    danger: decisionType === 'reject'
                }}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="comments"
                        label="Comments"
                        rules={[
                            {
                                required: decisionType === 'reject',
                                message: 'Please provide a reason for rejection'
                            }
                        ]}
                    >
                        <Input.TextArea
                            rows={4}
                            placeholder={
                                decisionType === 'approve'
                                    ? 'Optional comments...'
                                    : 'Please provide a reason for rejection...'
                            }
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default SubscriptionRequests;
